import React from 'react';
import PropTypes from 'prop-types';
import { faPlusCircle } from '@fortawesome/pro-solid-svg-icons/faPlusCircle';

// PropTypes
import ChecklistSchoolGroupProps from 'propTypes/checklistSchoolGroup';

// Utilities
import {
  commonChecklistGroups as groupsDuck,
  customerInfos as customerInfosDuck,
  modals
} from 'ducks';
import FontAwesome from 'utils/FA';
import { duckify } from 'utils/duckify';
import customerPath from 'utils/customerPath';

// Components
import LinkButton from 'components/LinkButton/LinkButton';
import ModalRoot from 'components/ModalRoot';
import Placeholder from 'components/Placeholder';
import ManageSchoolGroupModal from './ManageSchoolGroupModal';

const addItemButton = (
  <React.Fragment>
    <FontAwesome icon={faPlusCircle} postNbsp />
    Add a new school group
  </React.Fragment>
);

const ManageChecklistSchoolGroups = ({
  checklistSchoolGroups,
  customers,
  onDestroy,
  onSubmit,
  showModal
}) => (
  <div className='manage-school-groups'>
    <ModalRoot modals={{ ManageSchoolGroupModal }} />
    <h2>Manage School Groups</h2>
    <LinkButton
      className='add-item-button'
      content={addItemButton}
      onClick={() => showModal({ customers, onSubmit })}
    />

    <Placeholder.loading ready={customers.ready}>
      <table className='table'>
        <thead>
          <tr>
            <th>School Group Name</th>
            <th>Location</th>
            <th>Members</th>
            <th>Last Edited By</th>
            <th />
          </tr>
        </thead>
        <tbody>
          {
            checklistSchoolGroups.map(({
              id,
              name,
              location,
              customer_ids: customerIds,
              last_edited_by: lastEditedBy
            }) => (
              <tr key={id} data-group-id={id}>
                <td>{name}</td>
                <td>{location}</td>
                <td>
                  {
                    customerIds.map((customerId, i) => (
                      <React.Fragment key={customerId}>
                        <SchoolLink customerId={customerId} customers={customers} />
                        {(i < customerIds.length - 1) && ', '}
                      </React.Fragment>
                    ))
                  }
                </td>
                <td>{lastEditedBy}</td>
                <td>
                  <LinkButton
                    content='Edit'
                    onClick={() => showModal({
                      customers,
                      customerIds,
                      id,
                      name,
                      location,
                      onSubmit
                    })}
                  />
                  &nbsp;|&nbsp;
                  <LinkButton content='Delete' onClick={() => onDestroy(id, name)} />
                </td>
              </tr>
            ))
          }
        </tbody>
      </table>
    </Placeholder.loading>
  </div>
);

const SchoolLink = ({ customers, customerId }) => {
  const { subdomain } = customers.find((customer) => (customer.id === customerId));

  return <a href={customerPath(subdomain)}>{subdomain}</a>;
};

SchoolLink.propTypes = {
  customers: PropTypes.arrayOf(PropTypes.shape({
    id: PropTypes.string,
    subdomain: PropTypes.string
  })),
  customerId: PropTypes.string
};

SchoolLink.defaultProps = {
  customers: [],
  customerId: ''
};

ManageChecklistSchoolGroups.propTypes = {
  checklistSchoolGroups: PropTypes.arrayOf(PropTypes.shape(ChecklistSchoolGroupProps)),
  customers: PropTypes.arrayOf(PropTypes.shape({
    id: PropTypes.string,
    subdomain: PropTypes.string
  })),

  onDestroy: PropTypes.func,
  onSubmit: PropTypes.func,
  showModal: PropTypes.func
};

ManageChecklistSchoolGroups.defaultProps = {
  checklistSchoolGroups: [],
  customers: [],

  onDestroy: () => false,
  onSubmit: () => false,
  showModal: () => false
};

const mapDucksToProps = () => ({
  customers: customerInfosDuck,
  checklistSchoolGroups: groupsDuck
});

const mapDispatchToProps = (dispatch) => ({
  onDestroy: (id, name) => dispatch(
    modals.actions.show('VerificationModal', {
      title: 'Are you sure?',
      message: (
        <p>
          This will delete the group
          { ` "${name}". ` }
          Are you sure?
        </p>
      ),
      onConfirm: () => dispatch(groupsDuck.actions.remove({ id }))
    })
  ),
  onSubmit: (object, { setErrors }) => dispatch(groupsDuck.actions.save(object))
    .then(() => dispatch(groupsDuck.actions.load()))
    .then(() => dispatch(modals.actions.hide()))
    .catch(({ errors }) => setErrors(errors)),
  showModal: (props) => dispatch(
    modals.actions.show('ManageSchoolGroupModal', { ...props })
  )
});

export default duckify(mapDucksToProps, () => {}, mapDispatchToProps)(ManageChecklistSchoolGroups);
export { ManageChecklistSchoolGroups };
